/**
 * Where every record in this graph currently is.
 *
 * The pipeline has stages, and a record is sitting in exactly one of them. Until
 * now the Records screen made you click through three sub-tabs to find out which
 * — and a rejected record you never clicked "Rejected records" to see is a
 * record you do not know you have lost.
 *
 * So the counts come first, in pipeline order, and each one is the way into the
 * view that shows it. A count of zero is drawn quietly rather than hidden: "no
 * rejected records" is information, and a panel that disappears when it is empty
 * cannot tell you that.
 */

import type { ReactNode } from 'react'
import { Chip, type ChipTone } from '../components'
import { useRecordCounts } from '../hooks'

interface Tile {
  key: string
  label: string
  value: number
  hint: string
  tone?: ChipTone
  /** Which sub-view this count is about, when clicking should go there. */
  goes?: 'golden' | 'staged' | 'review'
}

export function CountsStrip({
  graphId,
  onOpen,
  extra,
}: {
  graphId: string
  onOpen?: (view: 'golden' | 'staged' | 'review') => void
  extra?: ReactNode
}) {
  const counts = useRecordCounts(graphId)

  if (counts.isLoading) {
    return (
      <div className="cp-counts" aria-busy="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div className="cp-count cp-count-skeleton" key={i} />
        ))}
      </div>
    )
  }
  // A failed count must not take the screen with it — the tables below are the
  // point, and they load independently.
  if (counts.error || !counts.data) return null

  const data = counts.data
  const tiles: Tile[] = [
    {
      key: 'new',
      label: 'New',
      value: data.staging.new,
      hint: 'Staged rows nothing has read yet. They clear on the next pipeline run.',
      tone: data.staging.new ? 'warn' : 'plain',
    },
    {
      key: 'staged',
      label: 'Staged',
      value: data.staging.total,
      hint: 'Every source row landed in staging, at whatever stage it reached.',
    },
    {
      key: 'pending',
      label: 'Awaiting resolution',
      value: data.staged_records.pending,
      hint: 'Extracted records that have not been matched into a golden record yet.',
      tone: data.staged_records.pending ? 'warn' : 'plain',
    },
    {
      key: 'rejected',
      label: 'Rejected',
      value: data.staged_records.rejected,
      hint: 'Held back by a quality rule you wrote. Fixable and resubmittable.',
      tone: data.staged_records.rejected ? 'err' : 'plain',
      goes: 'staged',
    },
    {
      key: 'matches',
      label: 'Potential matches',
      value: data.matches.potential,
      hint: 'Pairs the match rules scored into the grey band and left for a person.',
      tone: data.matches.potential ? 'warn' : 'plain',
      goes: 'review',
    },
    {
      key: 'golden',
      label: 'Golden records',
      value: data.records.golden,
      hint: 'What the pipeline produced, after matching and survivorship.',
      tone: data.records.golden ? 'ok' : 'plain',
      goes: 'golden',
    },
    {
      key: 'relationships',
      label: 'Relationships',
      value: data.relationships.total,
      hint: `Between records. ${data.relationships.unconnected_records.toLocaleString()} record${
        data.relationships.unconnected_records === 1 ? '' : 's'
      } connected to nothing.`,
      tone: data.relationships.total ? 'ok' : 'warn',
    },
  ]

  const queued = Object.values(data.queues ?? {}).reduce((a, b) => a + b, 0)

  return (
    <div className="cp-counts">
      {tiles.map((tile) => {
        const clickable = Boolean(tile.goes && onOpen)
        return (
          <button
            key={tile.key}
            type="button"
            className="cp-count"
            data-clickable={clickable || undefined}
            title={tile.hint}
            disabled={!clickable}
            onClick={() => tile.goes && onOpen?.(tile.goes)}
          >
            <span className="cp-count-n">{tile.value.toLocaleString()}</span>
            <span className="cp-count-l">{tile.label}</span>
            {tile.tone && tile.tone !== 'plain' && tile.value > 0 && (
              <span className={`cp-count-dot cp-count-${tile.tone}`} aria-hidden="true" />
            )}
          </button>
        )
      })}

      {queued > 0 && (
        <div className="cp-count cp-count-note">
          <Chip tone="warn">{queued.toLocaleString()} queued</Chip>
          <span className="cp-count-l">Run the pipeline to drain</span>
        </div>
      )}
      {extra}
    </div>
  )
}
